/**
 * MODULE: domain/sales-intelligence/frontend/components/CompanyProfileCard.tsx
 * PURPOSE: Visual company profile card displaying research insights and lead scoring
 *
 * DEPENDENCIES:
 *   - react: React library
 *   - ../../contracts/sales-intelligence.types: CompanyInsight type
 *   - useAccountActions: Export and share actions
 *   - ExportModal: Export format selection
 *
 * PUBLIC API:
 *   - CompanyProfileCard: Company insight display component
 *
 * SIDE EFFECTS:
 *   - Export actions trigger downloads; share copies link to clipboard
 *
 * AGENT SAFETY: SAFE
 * FILE SIZE: ~220 / 250 lines
 *
 * Recent: 2026-02-07 - Integrated export functionality (PDF, JSON, Markdown) and ExportModal
 * See: /README.md#documentation | /ARCHITECTURE.md | /domain/sales-intelligence/README.md
 */

import React, { useState } from 'react';
import { CompanyInsight } from '../../contracts/sales-intelligence.types';
import { useAccountActions } from '../hooks/useAccountActions';
import { ExportModal } from './ExportModal';

interface CompanyProfileCardProps {
  data: CompanyInsight;
  showActions?: boolean;
}

export function CompanyProfileCard({ data, showActions = true }: CompanyProfileCardProps) {
  const [showExportModal, setShowExportModal] = useState(false);
  const { exportToPDF, loading: isExporting, error: exportError } = useAccountActions();

  const handleQuickExport = async () => {
    try {
      await exportToPDF(data.id, data.company_name);
    } catch (err) {
      console.error('Quick export failed:', err);
    }
  };
  const getScoreColor = (score?: number) => {
    if (!score) return 'text-gray-600 bg-gray-50';
    if (score >= 80) return 'text-green-600 bg-green-50';
    if (score >= 60) return 'text-yellow-600 bg-yellow-50';
    return 'text-red-600 bg-red-50';
  };

  const getTierBadge = (tier?: string) => {
    const colors: Record<string, string> = {
      hot: 'bg-red-100 text-red-800',
      warm: 'bg-yellow-100 text-yellow-800',
      cold: 'bg-blue-100 text-blue-800',
    };
    return colors[tier || 'cold'] || colors.cold;
  };

  const leadScore = data.lead_score || 0;
  const priorityTier = data.priority_tier || 'cold';
  const keyInsights = Array.isArray(data.key_insights) ? data.key_insights : [];
  const signals = data.signals && typeof data.signals === 'object' ? data.signals : {};
  const researchData = data.research_data || {};

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm mt-4">
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div>
          <h3 className="text-xl font-bold text-gray-900">{data.company_name}</h3>
          {priorityTier && (
            <span className={`inline-block px-2 py-1 rounded text-xs font-semibold mt-2 ${getTierBadge(priorityTier)}`}>
              {priorityTier.toUpperCase()}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {leadScore > 0 && (
            <div className={`px-4 py-2 rounded-lg ${getScoreColor(leadScore)}`}>
              <div className="text-2xl font-bold">{leadScore}</div>
              <div className="text-xs">Lead Score</div>
            </div>
          )}
          {showActions && (
            <>
              <button
                onClick={handleQuickExport}
                disabled={isExporting}
                className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
                title="Quick export to PDF"
              >
                {isExporting ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-gray-600" />
                ) : (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                )}
              </button>
              <button
                onClick={() => setShowExportModal(true)}
                className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                </svg>
                Export & Share
              </button>
            </>
          )}
        </div>
      </div>

      {exportError && (
        <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-3">
          <p className="text-sm text-red-800">{exportError}</p>
        </div>
      )}

      {/* Research Summary */}
      {researchData.summary && (
        <div className="mb-4 pb-4 border-b">
          <h4 className="font-semibold text-gray-700 text-sm mb-2">Summary</h4>
          <p className="text-gray-600 text-sm">{researchData.summary}</p>
        </div>
      )}

      {/* Key Insights */}
      {keyInsights.length > 0 && (
        <div className="space-y-3 mb-4">
          <h4 className="font-semibold text-gray-700 text-sm mb-2">Key Insights</h4>
          {keyInsights.map((insight, index) => (
            <div key={index} className="border-l-4 border-blue-500 pl-4">
              <p className="text-gray-600 text-sm">{insight}</p>
            </div>
          ))}
        </div>
      )}

      {/* Company Info */}
      {(researchData.industry || researchData.size || researchData.headquarters) && (
        <div className="mb-4 pb-4 border-b">
          <h4 className="font-semibold text-gray-700 text-sm mb-2">Company Information</h4>
          <div className="grid grid-cols-2 gap-2 text-sm">
            {researchData.industry && (
              <div>
                <span className="text-gray-500">Industry:</span>
                <span className="text-gray-900 ml-2">{researchData.industry}</span>
              </div>
            )}
            {researchData.size && (
              <div>
                <span className="text-gray-500">Size:</span>
                <span className="text-gray-900 ml-2">{researchData.size}</span>
              </div>
            )}
            {researchData.headquarters && (
              <div>
                <span className="text-gray-500">Headquarters:</span>
                <span className="text-gray-900 ml-2">{researchData.headquarters}</span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Buying Signals */}
      {signals && Object.keys(signals).length > 0 && (
        <div className="mb-4">
          <h4 className="font-semibold text-gray-700 text-sm mb-2">Buying Signals</h4>
          <div className="flex flex-wrap gap-2">
            {Object.entries(signals).map(([key, value]) => {
              // Handle different signal types
              const hasSignals = Array.isArray(value) ? value.length > 0 : Boolean(value);
              if (!hasSignals) return null;
              
              return (
                <span 
                  key={key} 
                  className="px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-xs"
                >
                  {key.replace(/_/g, ' ')}
                </span>
              );
            })}
          </div>
        </div>
      )}

      {/* Source URLs */}
      {Array.isArray(data.source_urls) && data.source_urls.length > 0 && (
        <div className="mb-4">
          <h4 className="font-semibold text-gray-700 text-sm mb-2">Sources</h4>
          <div className="flex flex-wrap gap-2">
            {data.source_urls.slice(0, 3).map((url, index) => (
              <a
                key={index}
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-blue-600 hover:underline truncate max-w-xs"
              >
                {new URL(url).hostname}
              </a>
            ))}
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div className="mt-6 flex gap-2">
        <button className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium transition-colors">
          View Full Profile
        </button>
        {!showActions && (
          <button
            onClick={() => setShowExportModal(true)}
            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm font-medium transition-colors"
          >
            Export
          </button>
        )}
      </div>
      <ExportModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        companyName={data.company_name}
        insightId={data.id}
      />
    </div>
  );
}
